import React, { useEffect, useMemo, useState } from "react";
import { Card, Statistic, Tag, Tooltip, Button, Dropdown, Empty, type MenuProps } from "antd";
import {
  Wallet,
  Users,
  CircleDollarSign,
  TrendingUp,
  Settings,
  RefreshCw,
  Minus,
  Filter,
  ChevronDown,
} from "lucide-react";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
} from "recharts";
import Layout from "../../Layout";

import { DASHBOARD_DATA } from "./MockData";

/* ------------------------------------------------------------------ */
/*  Types                                                             */
/* ------------------------------------------------------------------ */

export interface SalaryKpi {
  totalSalaryCurrent: number;
  totalSalaryChangePercent: number;
  headcount: number;
  headcountChange: number;
  salaryPerHead: number;
  annualRunRate: number;
  comparePeriod: string;
}

export interface SalaryCompositionItem {
  label: string;
  amount: number;
  color: string;
}

export interface NamedValue {
  name: string;
  value: number;
}

export interface TrendPoint {
  period: string;
  value: number;
}

export interface SalaryDashboardData {
  kpi: SalaryKpi;
  composition: SalaryCompositionItem[];
  byDepartment: NamedValue[];
  byPosition: NamedValue[];
  byRole: NamedValue[];
  totalSalaryTrend: TrendPoint[];
  headcountTrend: TrendPoint[];
  avgSalaryTrend: TrendPoint[];
  yoyGrowth: TrendPoint[];
}

export interface SalaryDashboardProps {
  data?: SalaryDashboardData;
  currency?: string;
  className?: string;
}

/* ------------------------------------------------------------------ */
/*  Default (sample) data — replace via the `data` prop for live data */
/* ------------------------------------------------------------------ */



/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

const numberFmt = new Intl.NumberFormat("vi-VN");

function formatFull(n: number) {
  return numberFmt.format(Math.round(n));
}

function formatCompact(n: number) {
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(abs % 1_000_000 === 0 ? 0 : 1)}M`;
  if (abs >= 1_000) return `${(n / 1_000).toFixed(0)}k`;
  return `${n}`;
}

function usePrefersDark() {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const root = document.documentElement;
    const compute = () => setIsDark(root.classList.contains("dark"));
    compute();

    const observer = new MutationObserver(compute);
    observer.observe(root, { attributes: true, attributeFilter: ["class"] });

    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const onMedia = () => compute();
    media.addEventListener?.("change", onMedia);

    return () => {
      observer.disconnect();
      media.removeEventListener?.("change", onMedia);
    };
  }, []);

  return isDark;
}

/* ------------------------------------------------------------------ */
/*  Small building blocks                                              */
/* ------------------------------------------------------------------ */

interface PanelProps {
  title: string;
  withFilter?: boolean;
  children: React.ReactNode;
  className?: string;
}

const filterMenuItems: MenuProps["items"] = [
  { key: "this-period", label: "Kỳ này" },
  { key: "last-period", label: "Kỳ trước" },
  { key: "last-3", label: "3 kỳ gần nhất" },
  { key: "custom", label: "Tuỳ chỉnh..." },
];

function Panel({ title, withFilter, children, className = "" }: PanelProps) {
  return (
    <Card
      className={`!bg-white dark:!bg-slate-900 !border !border-gray-200 dark:!border-slate-700/70 !rounded-xl shadow-sm ${className}`}
      styles={{ body: { padding: "20px" } }}
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-[15px] font-medium text-gray-800 dark:text-slate-100">{title}</h3>
        <div className="flex items-center gap-1 text-gray-400 dark:text-slate-500">
          {withFilter && (
            <Dropdown menu={{ items: filterMenuItems }} trigger={["click"]}>
              <button
                type="button"
                className="flex items-center gap-0.5 p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-gray-600 dark:hover:text-slate-300 transition-colors"
              >
                <Filter size={14} />
                <ChevronDown size={12} />
              </button>
            </Dropdown>
          )}
          <Tooltip title="Làm mới">
            <button
              type="button"
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-gray-600 dark:hover:text-slate-300 transition-colors"
            >
              <RefreshCw size={14} />
            </button>
          </Tooltip>
          <Tooltip title="Thu gọn">
            <button
              type="button"
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-gray-600 dark:hover:text-slate-300 transition-colors"
            >
              <Minus size={14} />
            </button>
          </Tooltip>
        </div>
      </div>
      {children}
    </Card>
  );
}

interface KpiCardProps {
  icon: React.ReactNode;
  iconBg: string;
  value: string;
  label: string;
  changePercent?: number;
}

function KpiCard({ icon, iconBg, value, label, changePercent }: KpiCardProps) {
  const hasChange = typeof changePercent === "number";
  const positive = (changePercent ?? 0) >= 0;

  return (
    <div className="flex-1 min-w-[220px] flex items-center gap-4 rounded-xl border border-gray-200 dark:border-slate-700/70 bg-white dark:bg-slate-900 p-4">
      <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${iconBg}`}>
        {icon}
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <Statistic
            value={value}
            valueStyle={{
              fontSize: 20,
              fontWeight: 600,
              lineHeight: "24px",
              color: "inherit",
            }}
            className="text-gray-900 dark:text-slate-100"
          />
          {hasChange && (
            <Tag
              color={positive ? "success" : "error"}
              className="!m-0 !text-xs !leading-5 !rounded-full"
            >
              {positive ? "+" : ""}
              {changePercent}%
            </Tag>
          )}
        </div>
        <div className="text-sm text-gray-500 dark:text-slate-400 truncate">{label}</div>
      </div>
    </div>
  );
}

const CHART_PALETTE = ["#5B9BF0", "#3A3A3A", "#8BC98A", "#8E7CE0", "#F0A75B"];

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

export default function SalaryDashboard({
  data = DASHBOARD_DATA,
  className = "",
}: SalaryDashboardProps) {
  const isDark = usePrefersDark();
  const axisColor = isDark ? "#8592A8" : "#8C8C8C";
  const gridColor = isDark ? "#2A3446" : "#EDEFF2";
  const tooltipBg = isDark ? "#1E293B" : "#FFFFFF";
  const tooltipBorder = isDark ? "#334155" : "#E5E7EB";
  const tooltipText = isDark ? "#E2E8F0" : "#1F2937";

  const { kpi, composition, byDepartment, byPosition, byRole, totalSalaryTrend, headcountTrend, avgSalaryTrend, yoyGrowth } = data;

  const compositionTotal = useMemo(
    () => composition.reduce((sum, item) => sum + item.amount, 0),
    [composition]
  );

  const tooltipStyle = {
    backgroundColor: tooltipBg,
    border: `1px solid ${tooltipBorder}`,
    borderRadius: 8,
    color: tooltipText,
    fontSize: 12,
  };

  const barChartMargin = { top: 8, right: 8, bottom: 0, left: -12 };

  return (
    <Layout>
      <div className={`w-full space-y-4 sm:space-y-5 ${className}`}>
        {/* Tổng quan lương */}
        <Panel title="Tổng quan lương">
          <div className="flex flex-wrap gap-3 sm:gap-4">
            <KpiCard
              icon={<Wallet size={20} className="text-white" />}
              iconBg="bg-blue-400"
              value={formatFull(kpi.totalSalaryCurrent)}
              label="Tổng lương kỳ này"
              changePercent={kpi.totalSalaryChangePercent}
            />
            <KpiCard
              icon={<Users size={20} className="text-white" />}
              iconBg="bg-emerald-400"
              value={`${kpi.headcount}`}
              label="Số nhân sự"
              changePercent={kpi.headcountChange}
            />
            <KpiCard
              icon={<CircleDollarSign size={20} className="text-white" />}
              iconBg="bg-amber-400"
              value={formatFull(kpi.salaryPerHead)}
              label="Lương / nhân sự"
            />
            <KpiCard
              icon={<TrendingUp size={20} className="text-white" />}
              iconBg="bg-rose-400"
              value={formatFull(kpi.annualRunRate)}
              label="Lương năm (run-rate)"
            />
          </div>
          <div className="flex items-center gap-2 mt-4 text-xs text-gray-400 dark:text-slate-500">
            <Settings size={12} />
            <span>So với kỳ {kpi.comparePeriod}</span>
          </div>
        </Panel>

        {/* Cơ cấu lương / Lương theo phòng ban */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-5">
          <Panel title="Cơ cấu lương">
            <div className="h-[260px] sm:h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={composition}
                    dataKey="amount"
                    nameKey="label"
                    innerRadius={0}
                    outerRadius="85%"
                    label={({ label, amount }) =>
                      `${label}: ${((amount / compositionTotal) * 100).toFixed(1)}%`
                    }
                    labelLine
                  >
                    {composition.map((entry) => (
                      <Cell key={entry.label} fill={entry.color} stroke={isDark ? "#0F172A" : "#fff"} strokeWidth={2} />
                    ))}
                  </Pie>
                  <RechartsTooltip
                    contentStyle={tooltipStyle}
                    formatter={(value: number, name: string) => [formatFull(value), name]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </Panel>

          <Panel title="Lương theo phòng ban" withFilter>
            <div className="h-[260px] sm:h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={byDepartment} margin={barChartMargin}>
                  <CartesianGrid vertical={false} stroke={gridColor} />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 11, fill: axisColor }}
                    angle={-20}
                    textAnchor="end"
                    height={60}
                    interval={0}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: axisColor }}
                    tickFormatter={(v) => formatCompact(v)}
                  />
                  <RechartsTooltip
                    contentStyle={tooltipStyle}
                    formatter={(value: number) => formatFull(value)}
                  />
                  <Bar dataKey="value" radius={[4, 4, 4, 4]}>
                    {byDepartment.map((entry, i) => (
                      <Cell key={entry.name} fill={CHART_PALETTE[i % CHART_PALETTE.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Panel>
        </div>

        {/* Lương theo vị trí / Lương theo chức vụ */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-5">
          <Panel title="Lương theo vị trí" withFilter>
            <div className="h-[260px] sm:h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={byPosition} margin={barChartMargin}>
                  <CartesianGrid vertical={false} stroke={gridColor} />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 10, fill: axisColor }}
                    angle={-25}
                    textAnchor="end"
                    height={70}
                    interval={0}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: axisColor }}
                    tickFormatter={(v) => formatCompact(v)}
                  />
                  <RechartsTooltip
                    contentStyle={tooltipStyle}
                    formatter={(value: number) => formatFull(value)}
                  />
                  <Bar dataKey="value" radius={[4, 4, 4, 4]}>
                    {byPosition.map((entry, i) => (
                      <Cell key={entry.name} fill={CHART_PALETTE[i % CHART_PALETTE.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Panel>

          <Panel title="Lương theo chức vụ" withFilter>
            <div className="h-[260px] sm:h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={byRole} margin={barChartMargin}>
                  <CartesianGrid vertical={false} stroke={gridColor} />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 11, fill: axisColor }}
                    angle={-20}
                    textAnchor="end"
                    height={60}
                    interval={0}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: axisColor }}
                    tickFormatter={(v) => formatCompact(v)}
                  />
                  <RechartsTooltip
                    contentStyle={tooltipStyle}
                    formatter={(value: number) => formatFull(value)}
                  />
                  <Bar dataKey="value" radius={[4, 4, 4, 4]}>
                    {byRole.map((entry, i) => (
                      <Cell key={entry.name} fill={CHART_PALETTE[i % CHART_PALETTE.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Panel>
        </div>

        {/* Biến động tổng lương / nhân sự theo kỳ */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-5">
          <Panel title="Biến động tổng lương theo kỳ" withFilter>
            <div className="h-[240px] sm:h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={totalSalaryTrend} margin={{ top: 8, right: 12, bottom: 0, left: -12 }}>
                  <CartesianGrid vertical={false} stroke={gridColor} />
                  <XAxis dataKey="period" tick={{ fontSize: 11, fill: axisColor }} />
                  <YAxis tick={{ fontSize: 11, fill: axisColor }} tickFormatter={(v) => formatCompact(v)} />
                  <RechartsTooltip contentStyle={tooltipStyle} formatter={(value: number) => formatFull(value)} />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#5B9BF0"
                    strokeWidth={2}
                    dot={{ r: 4, fill: "#5B9BF0" }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Panel>

          <Panel title="Biến động nhân sự theo kỳ" withFilter>
            <div className="h-[240px] sm:h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={headcountTrend} margin={{ top: 8, right: 12, bottom: 0, left: -12 }}>
                  <CartesianGrid vertical={false} stroke={gridColor} />
                  <XAxis dataKey="period" tick={{ fontSize: 11, fill: axisColor }} />
                  <YAxis
                    tick={{ fontSize: 11, fill: axisColor }}
                    domain={["dataMin - 1", "dataMax + 1"]}
                    allowDecimals={false}
                  />
                  <RechartsTooltip contentStyle={tooltipStyle} formatter={(value: number) => `${value} người`} />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#5B9BF0"
                    strokeWidth={2}
                    dot={{ r: 4, fill: "#5B9BF0" }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Panel>
        </div>

        {/* Biến động lương trung bình / Tăng trưởng lương YoY */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-5">
          <Panel title="Biến động lương trung bình theo kỳ" withFilter>
            <div className="h-[240px] sm:h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={avgSalaryTrend} margin={{ top: 8, right: 12, bottom: 0, left: -12 }}>
                  <CartesianGrid vertical={false} stroke={gridColor} />
                  <XAxis dataKey="period" tick={{ fontSize: 11, fill: axisColor }} />
                  <YAxis tick={{ fontSize: 11, fill: axisColor }} tickFormatter={(v) => formatCompact(v)} />
                  <RechartsTooltip contentStyle={tooltipStyle} formatter={(value: number) => formatFull(value)} />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#5B9BF0"
                    strokeWidth={2}
                    dot={{ r: 4, fill: "#5B9BF0" }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Panel>

          <Panel title="Tăng trưởng lương so cùng kỳ năm trước" withFilter>
            <div className="h-[240px] sm:h-[280px] flex items-center justify-center">
              {yoyGrowth.length === 0 ? (
                <Empty
                  description={
                    <span className="text-gray-400 dark:text-slate-500 text-sm">
                      Không có dữ liệu hiển thị
                    </span>
                  }
                />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={yoyGrowth} margin={{ top: 8, right: 12, bottom: 0, left: -12 }}>
                    <CartesianGrid vertical={false} stroke={gridColor} />
                    <XAxis dataKey="period" tick={{ fontSize: 11, fill: axisColor }} />
                    <YAxis tick={{ fontSize: 11, fill: axisColor }} tickFormatter={(v) => `${v}%`} />
                    <RechartsTooltip contentStyle={tooltipStyle} formatter={(value: number) => `${value}%`} />
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke="#5B9BF0"
                      strokeWidth={2}
                      dot={{ r: 4, fill: "#5B9BF0" }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </Panel>
        </div>
      </div>
    </Layout>
  );
}